import React from 'react';
import { UsersThree } from '@phosphor-icons/react';
import { Badge } from '../ui/badge';

/**
 * Labor breakdown tab for AI Pricing Tool.
 * Receives displayEstimate, selectedTier, isEditing, and the handleLaborChange handler.
 * Stateless presentational component.
 */
export default function LaborTab({ displayEstimate, selectedTier, isEditing, handleLaborChange }) {
  if (!displayEstimate?.labor_breakdown) return null;
  const lb = displayEstimate.labor_breakdown;
  return (
    <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F]">
      <div className="p-4 border-b-2 border-black dark:border-white bg-neutral-50 dark:bg-neutral-900 flex items-center justify-between">
        <h4 className="font-black text-neutral-900 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2">
          <UsersThree size={18} weight="bold" className="text-[#2B4C6F]" />
          LABOR BREAKDOWN
          {isEditing && <Badge className="bg-amber-500 text-white text-[10px] ml-2">EDITING</Badge>}
        </h4>
      </div>
      <div className="p-5">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-neutral-200 dark:border-neutral-700">
              <span className="text-neutral-600 dark:text-neutral-400">Estimated Hours</span>
              {isEditing ? (
                <input
                  type="number"
                  value={lb.hours_estimated}
                  onChange={(e) => handleLaborChange('hours_estimated', parseFloat(e.target.value) || 0)}
                  className="w-20 text-right font-mono font-bold text-neutral-900 dark:text-white bg-transparent border-b-2 border-dashed border-neutral-400 focus:border-[#2B4C6F] outline-none"
                />
              ) : (
                <span className="font-mono font-bold text-neutral-900 dark:text-white">{lb.hours_estimated} hrs</span>
              )}
            </div>
            <div className="flex justify-between items-center py-2 border-b border-neutral-200 dark:border-neutral-700">
              <span className="text-neutral-600 dark:text-neutral-400">Crew Size</span>
              {isEditing ? (
                <input
                  type="number"
                  value={lb.crew_size}
                  onChange={(e) => handleLaborChange('crew_size', parseInt(e.target.value, 10) || 1)}
                  className="w-16 text-right font-mono font-bold text-neutral-900 dark:text-white bg-transparent border-b-2 border-dashed border-neutral-400 focus:border-[#2B4C6F] outline-none"
                />
              ) : (
                <span className="font-mono font-bold text-neutral-900 dark:text-white">{lb.crew_size} workers</span>
              )}
            </div>
            <div className="flex justify-between items-center py-2 border-b border-neutral-200 dark:border-neutral-700">
              <span className="text-neutral-600 dark:text-neutral-400">Skill Level</span>
              <span className="font-bold text-neutral-900 dark:text-white uppercase">{lb.skill_level}</span>
            </div>
          </div>

          <div className="bg-neutral-50 dark:bg-neutral-900 border-2 border-black dark:border-white p-4">
            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-3">Hourly Rates by Tier</p>
            <div className="space-y-2">
              <RateRow
                tierLabel="Economy"
                tierKey="economy"
                selectedTier={selectedTier}
                rate={lb.hourly_rate_low}
                isEditing={isEditing}
                onChange={(v) => handleLaborChange('hourly_rate_low', v)}
              />
              <RateRow
                tierLabel="Standard"
                tierKey="standard"
                selectedTier={selectedTier}
                rate={lb.hourly_rate_mid}
                isEditing={isEditing}
                onChange={(v) => handleLaborChange('hourly_rate_mid', v)}
              />
              <RateRow
                tierLabel="Premium"
                tierKey="premium"
                selectedTier={selectedTier}
                rate={lb.hourly_rate_high}
                isEditing={isEditing}
                onChange={(v) => handleLaborChange('hourly_rate_high', v)}
              />
            </div>
            <div className="mt-4 pt-4 border-t-2 border-black dark:border-white">
              <p className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Total Labor ({selectedTier})</p>
              <p className="text-2xl font-black text-[#2B4C6F] font-mono">
                ${(
                  selectedTier === 'economy' ? lb.total_labor_low
                  : selectedTier === 'premium' ? lb.total_labor_high
                  : lb.total_labor_mid
                )?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RateRow({ tierLabel, tierKey, selectedTier, rate, isEditing, onChange }) {
  const active = selectedTier === tierKey;
  return (
    <div className="flex justify-between items-center">
      <span className={active ? 'font-bold text-[#2B4C6F]' : 'text-neutral-600'}>{tierLabel}</span>
      {isEditing ? (
        <div className="flex items-center">
          <span className="text-neutral-500 mr-1">$</span>
          <input
            type="number"
            value={rate}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
            className="w-16 text-right font-mono bg-transparent border-b border-dashed border-neutral-300 focus:border-[#2B4C6F] outline-none"
          />
          <span className="text-neutral-500">/hr</span>
        </div>
      ) : (
        <span className="font-mono">${rate}/hr</span>
      )}
    </div>
  );
}
